import api from "@/services/api"
import type { MaybeRef } from "@vueuse/core"
import { useQuery } from "@tanstack/vue-query"
import { BASE_TRANSACTIONS_FEED_QUERY_KEY } from "@/services/queryKeys"
import type { RecursivelyConvertDatesToStrings } from "@/utils/typeUtilities"
import type {
  PaymentDetailCard,
  PaymentDetailCash,
  PaymentDetailSplit,
} from "../sharedTypes/invoices"
import type { CursorPaginationQuery } from "../sharedTypes/pagination"

export type TransactionsFeedFilters = {
  date?: string
  search?: string
  deviceSerialNumber?: string | null
}

export type TransactionsFeedCursor = {
  cursorAt?: string | null
  cursorPriority?: number | null
}

export type TransactionsFeedQuery =
  TransactionsFeedFilters &
    CursorPaginationQuery<TransactionsFeedCursor>

export type TransactionsFeedOrder = {
  id: string
  note: string | null
  numberOfItems: number
  createdAt: Date
  totalCost: number
  cashierId: string | null
  invoice: {
    id: string
    amountGross: number
    currency: string
    createdAt: Date
    isPaid: boolean
    paidBy:
      | PaymentDetailCard
      | PaymentDetailCash
      | PaymentDetailSplit
      | null
    paidAt: Date | null
  } | null
  orderNumber: number | null
}

export type TransactionsFeedZReport = {
  id: string
  createdAt: Date
  deviceSerialNumber: string
  fromReceiptNo: string | null
  toReceiptNo: string | null
  totalCount: number | null
  totalAmount: number | null
}

export type TransactionsFeedItem =
  | {
      kind: "order"
      order: TransactionsFeedOrder
      zReport: null
      createdAt: Date
    }
  | {
      kind: "z_report"
      order: null
      zReport: TransactionsFeedZReport
      createdAt: Date
    }

type TransactionsFeedResponse = {
  items: TransactionsFeedItem[]
  hasNext: boolean
  nextCursorAt: string | null
  nextCursorPriority: number | null
}

type RawResponse =
  RecursivelyConvertDatesToStrings<TransactionsFeedResponse>

const listTransactionsFeed = async (
  query?: TransactionsFeedQuery
): Promise<TransactionsFeedResponse> => {
  const { body } = await api.get<RawResponse>(
    "/transactions-feed/",
    { query }
  )

  return {
    hasNext: body.hasNext,
    nextCursorAt: body.nextCursorAt,
    nextCursorPriority: body.nextCursorPriority,
    items: body.items.map((item) => {
      if (item.kind === "order" && item.order) {
        return {
          kind: "order" as const,
          zReport: null,
          createdAt: DateFns.parseISO(item.createdAt),
          order: {
            ...item.order,
            createdAt: DateFns.parseISO(
              item.order.createdAt
            ),
            invoice: item.order.invoice && {
              ...item.order.invoice,
              createdAt: DateFns.parseISO(
                item.order.invoice.createdAt
              ),
              paidAt: item.order.invoice.paidAt
                ? DateFns.parseISO(
                    item.order.invoice.paidAt
                  )
                : null,
            },
          },
        }
      }
      return {
        kind: "z_report" as const,
        order: null,
        createdAt: DateFns.parseISO(item.createdAt),
        zReport: item.zReport && {
          ...item.zReport,
          createdAt: DateFns.parseISO(
            item.zReport.createdAt
          ),
        },
      }
    }),
  }
}

export const useTransactionsFeed = (
  query: MaybeRef<TransactionsFeedQuery>
) =>
  useQuery(
    [BASE_TRANSACTIONS_FEED_QUERY_KEY, query] as const,
    ({ queryKey: [_key, _query] }) =>
      listTransactionsFeed(unref(_query)),
    {
      refetchInterval: 60 * 1000,
      staleTime: 0,
    }
  )
